import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronRight, ChevronLeft, CheckCircle2, Sparkles, BookOpen, ScrollText, FileSpreadsheet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, label: 'البيانات الشخصية' },
  { id: 2, label: 'قنوات التواصل' },
  { id: 3, label: 'التحدّي القرائي' },
];

const RegistrationPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [schema, setSchema] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/registrations/form-schema`).then(r => setSchema(r.data)).catch(() => setSchema(null));
  }, []);

  const fields = (schema?.fields || []).filter(f => f.step === step);
  const allFields = schema?.fields || [];

  const setVal = (id, v) => setValues(p => ({ ...p, [id]: v }));

  const validateStep = () => {
    for (const f of fields) {
      if (f.required && !String(values[f.id] || '').trim()) {
        toast.error(`الحقل "${f.label}" مطلوب`);
        return false;
      }
      if (f.type === 'email' && values[f.id] && !/^\S+@\S+\.\S+$/.test(values[f.id])) {
        toast.error(`صيغة البريد غير صحيحة`); return false;
      }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep(s => Math.min(STEPS.length, s + 1)); };
  const prev = () => setStep(s => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = {};
      allFields.forEach(f => { payload[f.id] = values[f.id] || ''; });
      await axios.post(`${API}/registrations`, payload);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'تعذّر إرسال الطلب');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center px-4" style={{background:'linear-gradient(135deg,#faf6f1,#f0e6d2)'}}>
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center" data-testid="registration-done">
          <CheckCircle2 className="w-20 h-20 mx-auto mb-4" style={{color:'#3A5F0B'}} />
          <h2 className="text-2xl font-bold mb-3" style={{color:'#5D4037'}}>تم استلام طلبك</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            شكراً لاهتمامك بالمبادرة. ستقوم الإدارة بمراجعة طلبك والتواصل معك عبر الواتساب فور اعتماد التسجيل ومنحك رقماً قرائياً.
          </p>
          <button onClick={() => navigate('/')} className="px-6 py-3 rounded-xl font-bold text-white shadow-lg hover:opacity-90 transition" style={{background:'#C19A6B'}} data-testid="registration-done-back">
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  if (!schema) {
    return <div dir="rtl" className="min-h-screen flex items-center justify-center"><div className="text-gray-500">جارٍ التحميل...</div></div>;
  }

  return (
    <div dir="rtl" className="min-h-screen px-4 py-8 sm:py-12" style={{background:'linear-gradient(135deg,#faf6f1,#f0e6d2)'}}>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Sparkles className="w-12 h-12 mx-auto mb-2" style={{color:'#C19A6B'}} />
          <h1 className="text-3xl sm:text-4xl font-bold" style={{color:'#5D4037'}}>التسجيل في المبادرة</h1>
          <p className="text-gray-600 mt-2 text-sm">انضمّ إلى مبادرة غصن الزيتون القرائية</p>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${step >= s.id ? 'text-white shadow-lg' : 'bg-gray-200 text-gray-500'}`} style={step >= s.id ? {background:'#C19A6B'} : {}} data-testid={`step-indicator-${s.id}`}>
                    {step > s.id ? <CheckCircle2 className="w-5 h-5" /> : s.id}
                  </div>
                  <span className={`text-[11px] sm:text-xs mt-2 text-center ${step >= s.id ? 'font-bold' : 'text-gray-400'}`} style={step >= s.id ? {color:'#5D4037'} : {}}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-1 mx-2 rounded-full" style={{background: step > s.id ? '#C19A6B' : '#e5e5e5'}} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border" style={{borderColor:'#C19A6B22'}}>
          <h2 className="text-xl font-bold mb-6" style={{color:'#5D4037'}}>{STEPS[step-1].label}</h2>

          <div className="space-y-5">
            {fields.map(f => (
              <div key={f.id}>
                <label className="block font-semibold mb-2 text-sm" style={{color:'#5D4037'}}>
                  {f.label} {f.required && <span className="text-red-500">*</span>}
                </label>
                {f.type === 'select' ? (
                  <select value={values[f.id] || ''} onChange={e => setVal(f.id, e.target.value)} className="w-full border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 transition" style={{borderColor:'#C19A6B55'}} data-testid={`field-${f.id}`}>
                    <option value="">-- اختر --</option>
                    {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={f.type || 'text'} placeholder={f.placeholder || ''} value={values[f.id] || ''} onChange={e => setVal(f.id, e.target.value)} className="w-full border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 transition" style={{borderColor:'#C19A6B55'}} data-testid={`field-${f.id}`} />
                )}
              </div>
            ))}
          </div>

          {/* Info box on step 3 */}
          {step === 3 && schema.info_box && (
            <div className="mt-6 rounded-xl p-5 border-2 shadow-inner" style={{background:'linear-gradient(135deg,#fef9f0,#fff7e6)', borderColor:'#C19A6B'}} data-testid="challenge-info-box">
              <div className="flex items-center gap-2 mb-3 font-bold" style={{color:'#5D4037'}}>
                <Sparkles className="w-5 h-5" style={{color:'#C19A6B'}} />
                <span>يكتمل التحدّي السنوي على ما يلي:</span>
              </div>
              <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans" style={{color:'#5D4037'}}>{schema.info_box}</pre>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-8 gap-3">
            <button onClick={() => navigate('/')} className="text-sm text-gray-500 hover:text-gray-700" data-testid="registration-cancel-btn">← إلغاء</button>
            <div className="flex gap-2">
              {step > 1 && (
                <button onClick={prev} className="px-5 py-2.5 rounded-xl font-bold border-2 transition hover:bg-gray-50" style={{borderColor:'#C19A6B', color:'#5D4037'}} data-testid="registration-prev-btn">
                  <ChevronRight className="w-4 h-4 inline-block ml-1" />السابق
                </button>
              )}
              {step < STEPS.length ? (
                <button onClick={next} className="px-6 py-2.5 rounded-xl font-bold text-white shadow-lg hover:opacity-90 transition" style={{background:'#C19A6B'}} data-testid="registration-next-btn">
                  التالي<ChevronLeft className="w-4 h-4 inline-block mr-1" />
                </button>
              ) : (
                <button onClick={submit} disabled={submitting} className="px-6 py-2.5 rounded-xl font-bold text-white shadow-lg hover:opacity-90 transition disabled:opacity-50" style={{background:'#3A5F0B'}} data-testid="registration-submit-btn">
                  {submitting ? 'جارٍ الإرسال...' : 'إرسال الطلب'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer icons (decorative) */}
        <div className="flex items-center justify-center gap-6 mt-8 text-gray-400">
          <BookOpen className="w-6 h-6" /><ScrollText className="w-6 h-6" /><FileSpreadsheet className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
